import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMobileContext } from '../../../contexts/MobileContext';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const getOnceOffFee = (amount, feeBands = []) => {
  const feeBand = feeBands.find(({ min = 0, max = Infinity }) => {
    return amount >= min && amount <= max;
  });

  if (feeBand) {
    return feeBand.fee;
  }

  return 0;
};

const sanitizeAmount = (value) =>
  String(value || '')
    .replace(/,/g, '')
    .replace(/R/gi, '')
    .replace(/[^\d.]/g, '')
    .trim();

const formatCurrency = (value) =>
  `R${Math.round(value || 0).toLocaleString('en-ZA')}`;

const formatCurrencyAmount = (value) =>
  Math.round(value || 0).toLocaleString('en-ZA');

const calculateLoan = (amount, termYears, calculationConfig) => {
  const {
    annualInterestRate,
    monthlyFixedServiceFee,
    variableServiceFeePerThousand,
    onceOffFees,
  } = calculationConfig;
  const onceOffFee = getOnceOffFee(amount, onceOffFees);
  const monthlyRate = annualInterestRate / 100 / 12;
  const periodMonths = 12 * termYears;
  const compoundFactor = (1 + monthlyRate) ** periodMonths;
  const financedAmount = amount + onceOffFee;
  const monthlyServiceFee =
    monthlyFixedServiceFee +
    (financedAmount / 1000) * variableServiceFeePerThousand;
  const monthlyRepayment =
    (financedAmount * compoundFactor * monthlyRate) / (compoundFactor - 1) +
    monthlyServiceFee;
  const totalRepayment = monthlyRepayment * periodMonths;

  return {
    monthlyRepayment,
    totalRepayment,
    monthlyServiceFee,
    totalInterest: totalRepayment - amount,
    termYears,
  };
};

const mergeCalculatorData = (data) => ({
  ...data,
  result: {
    ...(data?.result || {}),
  },
  assets: {
    ...(data?.assets || {}),
  },
  calculation: {
    ...(data?.calculation || {}),
  },
});

const PersonalLoanCalculator = ({ data, quoteRoute }) => {
  const calculatorData = mergeCalculatorData(data);
  const {
    title,
    description,
    inputLabel,
    inputPlaceholder,
    inputHelperText,
    emptyAmountError,
    maxAmountError,
    incrementAmountError,
    calculateButtonText,
    terms = [],
    defaultTerm,
    footnote,
    disclaimer,
    ctaText,
    result: resultConfig,
    assets,
    calculation,
  } = calculatorData;
  const resolvedQuoteRoute = quoteRoute || calculatorData.quoteRoute;
  const navigate = useNavigate();
  const { isDesktop } = useMobileContext();
  const [amountInput, setAmountInput] = useState('');
  const [selectedTerm, setSelectedTerm] = useState(
    defaultTerm ?? terms[0] ?? null
  );
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleCalculate = () => {
    const amount = parseFloat(sanitizeAmount(amountInput));

    if (!Number.isFinite(amount) || amount <= 0) {
      setResult(null);
      setError(emptyAmountError);
      return;
    }

    if (amount > calculation.maxLoanAmount) {
      setResult(null);
      setError(maxAmountError);
      return;
    }

    if (
      calculation.loanAmountIncrement &&
      amount % calculation.loanAmountIncrement !== 0
    ) {
      setResult(null);
      setError(incrementAmountError);
      return;
    }

    setError('');
    setResult(calculateLoan(amount, selectedTerm, calculation));
  };

  const handleAmountChange = (event) => {
    setAmountInput(sanitizeAmount(event.target.value));
    if (error) setError('');
  };

  const handleCtaClick = () => {
    navigate(resolvedQuoteRoute);
    window.scrollTo(0, 0);
  };

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        px: isDesktop ? '24px' : 0,
        maxWidth: '100vw',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1231px',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: isDesktop ? '1072px' : '100%',
            height: isDesktop ? (result ? '759px' : '417px') : 'auto',
            transition: isDesktop ? 'height 250ms ease' : 'none',
            backgroundColor: '#E3E6E8',
            borderRadius: '34px',
            p: isDesktop ? '32.5px 32px' : '32px 16px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: isDesktop ? '40px' : '24px',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <ScrollFadeWrapper variant="text">
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: isDesktop ? '3rem' : '2.25rem',
                  lineHeight: isDesktop ? '3.25rem' : '2.5rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                  textAlign: 'center',
                }}
              >
                {title}
              </Typography>
            </ScrollFadeWrapper>

            <ScrollFadeWrapper variant="text" delay={0.1}>
              <Typography
                component="p"
                sx={{
                  mt: isDesktop ? '16px' : '12px',
                  px: isDesktop ? 0 : '20px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: isDesktop ? '1.25rem' : '1.125rem',
                  lineHeight: isDesktop ? '1.75rem' : '1.5rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                  textAlign: 'center',
                }}
              >
                {description}
              </Typography>
            </ScrollFadeWrapper>
          </Box>

          <Box
            sx={{
              width: isDesktop ? '1231px' : '100%',
              position: isDesktop ? 'absolute' : 'relative',
              top: isDesktop ? (result ? '152px' : '131px') : 'auto',
              left: isDesktop ? '50%' : 'auto',
              transform: isDesktop ? 'translateX(-50%)' : 'none',
              zIndex: 2,
            }}
          >
            <ScrollFadeWrapper sx={{ width: '100%' }} delay={0.15}>
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '24px',
                  boxShadow:
                    '0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F',
                  boxSizing: 'border-box',
                  height: isDesktop ? (result ? '471px' : '201px') : 'auto',
                  transition: isDesktop ? 'height 250ms ease' : 'none',
                  p: '16px',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isDesktop
                      ? '377px max-content 170px'
                      : '1fr',
                    columnGap: isDesktop ? '16px' : 0,
                    rowGap: '24px',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <TextField
                      fullWidth
                      label={inputLabel}
                      value={amountInput}
                      onChange={handleAmountChange}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleCalculate();
                      }}
                      variant="outlined"
                      margin="none"
                      InputLabelProps={{
                        shrink: true,
                        sx: {
                          color: '#768690',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ mr: 0 }}>
                            <Box
                              component="span"
                              aria-hidden="true"
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 400,
                                fontSize: '1.25rem',
                                lineHeight: '1.5rem',
                                color: '#162F3B',
                              }}
                            >
                              {inputPlaceholder}
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        name: 'personal-loan-amount',
                        inputMode: 'numeric',
                        autoComplete: 'new-password',
                        autoCorrect: 'off',
                        autoCapitalize: 'off',
                        spellCheck: false,
                        'aria-label': inputLabel,
                      }}
                      sx={{
                        width: '100%',
                        maxWidth: isDesktop ? '377px' : 'none',
                        minWidth: 0,
                        height: '56px',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                          height: 'auto',
                          paddingTop: '16px',
                          paddingRight: '12px',
                          paddingBottom: '16px',
                          paddingLeft: '12px',
                          gap: '8px',
                          borderWidth: '1px',
                          '& fieldset': {
                            borderRadius: '16px',
                            borderWidth: '1px',
                            borderColor: '#A2ACB1',
                          },
                          '& input': {
                            padding: 0,
                            height: 'auto',
                            boxSizing: 'border-box',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '1.25rem',
                            lineHeight: '1.5rem',
                            color: '#162F3B',
                            '&::placeholder': {
                              color: '#162F3B',
                              opacity: 1,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '1.25rem',
                              lineHeight: '1.5rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          },
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        mt: '10px',
                        pl: '12px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: error ? '#D60000' : '#9AA8AF',
                      }}
                    >
                      {error || inputHelperText}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: isDesktop
                        ? 'repeat(3, 160px)'
                        : '1fr',
                      gap: isDesktop ? '16px 35px' : '12px',
                      justifyContent: isDesktop ? 'start' : 'stretch',
                      pr: isDesktop ? '19px' : 0,
                      minWidth: 0,
                    }}
                  >
                    {terms.map((term) => {
                      const isSelected = selectedTerm === term;

                      return (
                        <Button
                          key={term}
                          type="button"
                          onClick={() => setSelectedTerm(term)}
                          aria-pressed={isSelected}
                          disableElevation
                          sx={{
                            width: isDesktop ? '160px' : '100%',
                            minWidth: 0,
                            height: '52px',
                            borderRadius: '20px',
                            px: '24px',
                            py: '12px',
                            gap: '4px',
                            backgroundColor: isSelected ? '#C7CA00' : '#E3E6E8',
                            color: '#162F3B',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: isDesktop ? '1.5rem' : '1.125rem',
                            lineHeight: '1.5rem',
                            textTransform: 'none',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? '#C7CA00'
                                : '#D8DEE2',
                            },
                          }}
                        >
                          {term} {term === 1 ? 'year' : 'years'}
                        </Button>
                      );
                    })}
                  </Box>

                  <Button
                    type="button"
                    onClick={handleCalculate}
                    disableElevation
                    sx={{
                      width: isDesktop ? '147px' : '100%',
                      minWidth: 0,
                      height: isDesktop ? '40px' : 'auto',
                      minHeight: isDesktop ? '40px' : '60px',
                      alignSelf: isDesktop ? 'start' : 'stretch',
                      justifySelf: isDesktop ? 'start' : 'stretch',
                      mt: isDesktop ? '74px' : 0,
                      borderRadius: '16px',
                      px: isDesktop ? '12px' : '16px',
                      py: isDesktop ? '8px' : '18px',
                      backgroundColor: '#C7CA00',
                      color: '#162F3B',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.125rem',
                      lineHeight: isDesktop ? '1.25rem' : '1.5rem',
                      textTransform: 'none',
                      gap: '4px',
                      '&:hover': {
                        backgroundColor: '#C7CA00',
                      },
                    }}
                  >
                    {calculateButtonText}
                    <Image
                      width={22}
                      height={22}
                      src={assets.calculateButtonIcon}
                      alt=""
                      aria-hidden="true"
                      sx={{ width: '22px', height: '22px' }}
                    />
                  </Button>
                </Box>

                <Typography
                  sx={{
                    mt: isDesktop ? '34px' : '24px',
                    px: isDesktop ? '16px' : 0,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '11px',
                    lineHeight: '20px',
                    letterSpacing: '-0.002em',
                    color: '#162F3B',
                    textAlign: 'left',
                  }}
                >
                  {footnote}
                </Typography>

                <Collapse in={Boolean(result)} timeout={250} appear>
                  {result && (
                    <Box
                      sx={{
                        mt: isDesktop ? '16px' : '32px',
                        width: isDesktop ? '1212px' : '100%',
                        boxSizing: 'border-box',
                        borderTop: isDesktop ? 'none' : '1px solid #DDE2E6',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          p: isDesktop ? '8px 16px' : '16px',
                          height: isDesktop ? '203px' : 'auto',
                          boxSizing: 'border-box',
                          display: 'grid',
                          gridTemplateColumns: isDesktop
                            ? '249px 43px 360px 53px 40px 1fr'
                            : '1fr',
                          alignItems: 'center',
                          columnGap: 0,
                          rowGap: '28px',
                        }}
                      >
                        <Image
                          width={249}
                          height={187}
                          src={assets.hippoImage}
                          alt="Hippo mascot"
                          sx={{
                            gridColumn: isDesktop ? '1' : 'auto',
                            width: isDesktop ? '249px' : '170px',
                            height: isDesktop ? '187px' : '170px',
                            objectFit: 'contain',
                            display: 'block',
                            mx: isDesktop ? 0 : 'auto',
                            alignSelf: 'end',
                            justifySelf: isDesktop ? 'start' : 'center',
                          }}
                        />

                        <Box
                          sx={{
                            gridColumn: isDesktop ? '3' : 'auto',
                            width: isDesktop ? '360px' : '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 700,
                              fontSize: '3rem',
                              lineHeight: '3.25rem',
                              letterSpacing: '-0.03em',
                              color: '#162F3B',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                            }}
                          >
                            <Box component="span" sx={{ color: '#162F3B' }}>
                              R
                            </Box>
                            <Box component="span" sx={{ color: '#162F3B' }}>
                              {formatCurrencyAmount(result.monthlyRepayment)}
                            </Box>
                          </Typography>
                          <Typography
                            sx={{
                              mt: '4px',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '1.5rem',
                              letterSpacing: '-0.002em',
                              color: '#A2ACB1',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                            }}
                          >
                            {resultConfig.monthlyRepaymentLabel}
                          </Typography>
                          <Typography
                            sx={{
                              mt: '2px',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: '10px',
                              lineHeight: '1.5rem',
                              letterSpacing: '-0.002em',
                              color: '#A2ACB1',
                              textAlign: 'center',
                              verticalAlign: 'middle',
                            }}
                          >
                            {resultConfig.periodPrefix}{' '}
                            <Box component="span" sx={{ color: '#162F3B' }}>
                              {result.termYears}
                            </Box>{' '}
                            {resultConfig.periodSuffix}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            gridColumn: isDesktop ? '6' : 'auto',
                            maxWidth: isDesktop ? '455px' : '100%',
                            width: '100%',
                            mx: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: isDesktop
                                ? '250px 120px'
                                : '1fr auto',
                              gridTemplateRows: isDesktop
                                ? 'repeat(3, 1fr)'
                                : 'none',
                              rowGap: '4px',
                              columnGap: isDesktop ? '8px' : '4px',
                              alignItems: 'center',
                              justifyItems: 'start',
                              justifyContent: 'start',
                              width: '100%',
                              height: 'auto',
                            }}
                          >
                            {resultConfig.detailRows.map(({ key, label }) => (
                              <React.Fragment key={key}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 400,
                                    fontSize: isDesktop ? '1.25rem' : '1rem',
                                    lineHeight: '1.5rem',
                                    color: '#768690',
                                    textAlign: 'left',
                                  }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontWeight: 400,
                                    fontSize: isDesktop ? '1.25rem' : '1rem',
                                    lineHeight: '1.5rem',
                                    color: '#162F3B',
                                    textAlign: 'left',
                                    justifySelf: 'start',
                                  }}
                                >
                                  {formatCurrency(result[key])}
                                </Typography>
                              </React.Fragment>
                            ))}
                          </Box>
                          <Typography
                            sx={{
                              mt: '4px',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: '0.875rem',
                              lineHeight: '1.25rem',
                              color: '#768690',
                            }}
                          >
                            ({resultConfig.interestRateLabel}{' '}
                            {calculation.annualInterestRate}%)
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        aria-hidden="true"
                        sx={{
                          position: 'absolute',
                          display: isDesktop ? 'block' : 'none',
                          left: '721px',
                          top: '8px',
                          height: '187px',
                          width: '1px',
                          backgroundColor: '#DDE2E6',
                        }}
                      />

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mt: isDesktop ? 0 : '16px',
                        }}
                      >
                        <Button
                          onClick={handleCtaClick}
                          disableElevation
                          sx={{
                            width: isDesktop ? '202px' : '100%',
                            height: isDesktop ? '40px' : 'auto',
                            minHeight: isDesktop ? '40px' : '56px',
                            borderRadius: '16px',
                            px: isDesktop ? '12px' : '20px',
                            py: isDesktop ? '8px' : '16px',
                            backgroundColor: '#FF0074',
                            color: '#FFFFFF',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.5rem',
                            letterSpacing: '-0.002em',
                            verticalAlign: 'middle',
                            textTransform: 'none',
                            gap: '4px',
                            '&:hover': {
                              backgroundColor: '#FF0074',
                            },
                          }}
                        >
                          {ctaText}
                          <Image
                            width={22}
                            height={22}
                            src={assets.ctaButtonIcon}
                            alt=""
                            aria-hidden="true"
                            sx={{ width: '22px', height: '22px' }}
                          />
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Collapse>
              </Box>
            </ScrollFadeWrapper>
          </Box>

          <Typography
            sx={{
              position: isDesktop ? 'absolute' : 'relative',
              left: isDesktop ? '50%' : 'auto',
              bottom: isDesktop ? '32.5px' : 'auto',
              transform: isDesktop ? 'translateX(-50%)' : 'none',
              mt: isDesktop ? 0 : '28px',
              px: isDesktop ? 0 : '16px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              letterSpacing: '-0.002em',
              color: '#162F3B',
              textAlign: 'center',
              width: isDesktop ? '776px' : '100%',
              height: isDesktop ? '20px' : 'auto',
              maxWidth: isDesktop ? '776px' : '100%',
            }}
          >
            {disclaimer}
          </Typography>
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default PersonalLoanCalculator;
